import $ from 'jquery';

import {ArchiveRecord, BulkArchiveItemsResponse, BulkUnarchiveItemsResponse} from 'archive/ArchiveConstants';
import {DataModificationsHeader} from 'audit/DataModifications';
import {
  Collaborator,
  Owner,
  OwnerInfo,
  Permissions,
  CollaborationCreateOrUpdate,
} from 'auth/PermissionConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import * as SourceConstants from 'constants/SourceConstants';
import {FullSource, Source} from 'constants/SourceConstants';
import {refreshProjectsListing} from 'nav/sidepanel/listing/ProjectsListingActions';
import * as Perf from 'performance/Perf';
import {SearchResponse} from 'ui-library/entity-input/EntityInputWithSearch';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import FolderDAOCache from './FolderDAOCache';

const {PROJECT_TYPE} = SourceConstants;

function fetchSources(): void {
  const FileBrowserActions = require('./FileBrowserActions').default;
  FileBrowserActions.fetchSources();
  FolderDAOCache.invalidateFetchAll();
  refreshProjectsListing();
}

function fetchCollaborators(id: string): JQueryPromise<Array<Collaborator>> {
  return net.getJSON(`/1/api/sources/${id}/collaborators/`);
}

export interface UpdateSourceResponse extends Omit<FullSource, 'collaborators'> {
  collaborators: Array<Collaborator>;
}

export interface CreateParams {
  name: string;
  description?: string;
  collaborators?: Array<Collaborator>;
  collaborations?: Array<CollaborationCreateOrUpdate>;
  owner: Owner;
  ownerInfo?: OwnerInfo;
  reviewLifecycleApiIds?: Array<string>;
}

export interface UpdateData {
  name?: string;
  description?: string;
  public?: boolean;
  locked?: boolean;
  archived?: boolean;
  collaborators?: Array<Collaborator>;
  collaborations?: Array<CollaborationCreateOrUpdate>;
  owner?: string;
  ownerInfo?: OwnerInfo;
  alwaysShowAncestorFolderNames?: boolean;
  reviewLifecycleApiIds?: Array<string>;
}

export default {
  create(
    {name, description, owner, collaborators, collaborations, ownerInfo, reviewLifecycleApiIds}: CreateParams,
    param?: {parentFolderId?: string}
  ): JQueryPromise<Source & Pick<FullSource, 'permissions'>> {
    if (param == null) {
      param = {};
    }
    const {parentFolderId} = param;
    return net
      .post({
        url: '/1/api/sources/',
        data: $.param([
          {name: 'name', value: name},
          {name: 'description', value: description},
          {name: 'owner', value: owner.handle},
          {name: 'source_type', value: PROJECT_TYPE},
          {name: 'memberDataPolicyId', value: ownerInfo?.memberDataPolicyId},
          {name: 'collaborations', value: JSON.stringify(collaborations)},
          {name: 'review_lifecycle_api_ids', value: JSON.stringify(reviewLifecycleApiIds ?? [])},
        ]),
      })
      .done(() => {
        fetchSources(); // Also invalidates fetchAll
        FolderDAOCache.invalidateFolderContents(parentFolderId);
      });
  },

  delete_(id: string): JQueryPromise<void> {
    return TaskRunner.deleteJSON({
      url: `/1/api/sources/${id}`,
    }).done(() => {
      fetchSources();
    });
  },

  bulkArchive(
    ids: Array<string>,
    purpose: string,
    {
      shouldRemoveShortcuts,
      shouldUnregisterBioentities,
      unregisteredBioentitiesFolderId,
    }: {
      shouldRemoveShortcuts?: boolean;
      shouldUnregisterBioentities?: boolean;
      unregisteredBioentitiesFolderId?: string | null;
    },
    dataModsHeader?: DataModificationsHeader
  ): JQueryPromise<
    BulkArchiveItemsResponse & {
      permissionsByItemId: {[itemId: string]: Permissions};
      archiveRecordsByItemId: {[itemId: string]: ArchiveRecord};
      unregisterDataByItemId: {
        [itemId: string]: {id: string; name: string; bioentityAliasInfos: Array<BioentityAliasInfo>};
      };
    }
  > {
    return TaskRunner.postJSON({
      url: '/1/api/sources:archive',
      data: {
        ids,
        purpose,
        shouldRemoveShortcuts,
        shouldUnregisterBioentities,
        unregisteredBioentitiesFolderId,
      },
      extraHeaders: dataModsHeader,
    });
  },

  bulkUnarchive(
    ids: Array<string>,
    dataModsHeader?: DataModificationsHeader
  ): JQueryPromise<BulkUnarchiveItemsResponse & {permissionsByItemId: {[itemId: string]: Permissions}}> {
    return TaskRunner.postJSON({
      url: '/1/api/sources:unarchive',
      data: {ids},
      extraHeaders: dataModsHeader,
    });
  },

  star(sourceIds: Array<string>): JQueryPromise<{}> {
    return net
      .postJSON({
        url: '/1/api/sources/actions/star',
        data: {sourceIds},
      })
      .fail(() => {
        fetchSources();
      });
  },

  unstar(sourceIds: Array<string>): JQueryPromise<{}> {
    return net
      .postJSON({
        url: '/1/api/sources/actions/unstar',
        data: {sourceIds},
      })
      .fail(() => {
        fetchSources();
      });
  },

  hide(sourceIds: Array<string>): JQueryPromise<{}> {
    return net
      .postJSON({
        url: '/1/api/sources/actions/hide',
        data: {sourceIds},
      })
      .done(() => {
        fetchSources();
      });
  },

  unhide(sourceIds: Array<string>): JQueryPromise<{}> {
    return net
      .postJSON({
        url: '/1/api/sources/actions/unhide',
        data: {sourceIds},
      })
      .done(() => {
        fetchSources();
      });
  },

  update(
    id: string,
    data: UpdateData,
    dataModsHeader?: DataModificationsHeader
  ): Promise<UpdateSourceResponse> {
    return net
      .patchJSONPromise({
        url: `/1/api/sources/${id}`,
        data,
        extraHeaders: dataModsHeader,
      })
      .then((source) => {
        fetchSources();
        const rootFolderId = source.rootFolder.api_identifier;
        FolderDAOCache.invalidateFolder(rootFolderId);
        return source;
      });
  },

  setProjectReviewLifecycle(data: {
    projectId: string;
    reviewLifecycleApiIds: Array<string>;
    dataModsHeader?: DataModificationsHeader;
  }): Promise<UpdateSourceResponse> {
    let success = false;
    const perfId =
      Perf.start('set_project_review_lifecycle', {enabled: true, getMetadata: () => ({success})}) ?? '';
    Perf.markStart(perfId);

    // The user should not wait more than 10 seconds for the project settings modal to close while setProjectReviewLifecycle is running
    const updateWaitTimeMs = 10 * 1000;
    return Promise.resolve(
      TaskRunner.runTask(
        {
          contentType: 'application/json',
          data: JSON.stringify(data),
          dataType: 'json',
          type: 'PATCH',
          url: `/1/api/sources/${data.projectId}/review-lifecycle`,
        },
        updateWaitTimeMs,
        undefined,
        data.dataModsHeader
      )
    )
      .then((result) => {
        success = true;
        return result;
      })
      .finally(() => {
        Perf.markEnd(perfId);
        Perf.stop(perfId);
      });
  },

  bulkSetProjectsReviewLifecycle(data: {
    projectApiIds: Array<string>;
    reviewLifecycleApiIds: Array<string>;
  }): Promise<UpdateSourceResponse> {
    return Promise.resolve(
      TaskRunner.runTask({
        contentType: 'application/json',
        data: JSON.stringify(data),
        dataType: 'json',
        type: 'POST',
        url: `/1/api/sources/bulk-set-review-lifecycle`,
      })
    );
  },

  enableDataMods(projectApiIds: Array<string>): Promise<void> {
    return net.postJSONPromise({
      data: {projectApiIds},
      url: `/1/api/sources:enable-data-mods`,
    });
  },

  disableDataMods(projectApiIds: Array<string>): Promise<void> {
    return net.postJSONPromise({
      data: {projectApiIds},
      url: `/1/api/sources:disable-data-mods`,
    });
  },

  resetDataMods(): Promise<void> {
    return net.postJSONPromise({
      url: '/1/api/sources:reset-data-mods',
    });
  },

  fetchCollaborators,

  searchCollaboratorsForUsers(id: string, query: string, teamIds?: Array<string>): Promise<SearchResponse> {
    const teamIdsString = `&teamApiIds=${(teamIds ?? []).join(',')}`;
    return net.getJSONPromise(`/1/api/sources/${id}/collaborators:user_search?q=${query}${teamIdsString}`);
  },

  async bulkFetchCollaborators(
    this: void,
    sourceIds: Array<string>
  ): Promise<{[key: string]: Array<Collaborator>}> {
    // Note: It'd be nice to make this a bulk fetch endpoint.
    const collaboratorsBySourceId: {[key: string]: Array<Collaborator>} = {};
    for (const sourceId of sourceIds) {
      const collaborators = await fetchCollaborators(sourceId);
      collaboratorsBySourceId[sourceId] = collaborators!;
    }
    return collaboratorsBySourceId;
  },

  setCollaboratorForSources(
    data: {
      entityId: string;
      sourceIds: Array<string>;
      dataPolicyIds: Array<string>;
    },
    dataModsHeader?: DataModificationsHeader
  ): Promise<{}> {
    return net.postJSONPromise({
      url: '/1/api/sources/actions/share-sources-with-entity',
      data,
      extraHeaders: dataModsHeader,
    });
  },

  addCollaborator(id: string, data: {entity_id?: string}): JQueryPromise<{}> {
    return net.postJSON({
      url: `/1/api/sources/${id}/collaborators/`,
      data,
    });
  },

  removeCollaborator(id: string, partyId: string): Promise<{}> {
    return net.deletePromise({
      url: `/1/api/sources/${id}/collaborators/${partyId}`,
    });
  },
};
